import { useEffect, useState } from 'react';
import { AppVersionBadge } from './AppVersionBadge';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, X, Menu, LogOut, Building2, Home, Wrench } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
// I18N-2B (2026-09-23): hamburger chrome + registry labels via 'common'.
import { useTranslation } from 'react-i18next';

import { useEntitlements } from '../hooks/useEntitlements';
import { DESKTOP_NAVIGATION, FIXED_SIDEBAR_ITEM_KEYS, HAMBURGER_NAVIGATION, entitlementForStandaloneHome, filterNavigation, isEmployeeContextForLens, isStandaloneHomeUnit, navLabelKey } from '../config/navigation';
import type { NavigationItem } from '../config/navigation';
import { domainForModule, usePermissions } from '../hooks/usePermissions';

export default function MobileMenu({ messagesUnread = 0 }: { messagesUnread?: number }) {
  const [isOpen, setIsOpen] = useState(false);
  const { user, roles, logout, memberships, ownedUnits, activeCondoId, activeOwnedUnitId, setActiveCondo, setActiveOwnedUnit, contexts, activeContextId, showPersonalFlag } = useAuth();
  // (2026-09-05 fail-closed) permissionsLoading drives the menu skeleton.
  const { can, loading: permissionsLoading } = usePermissions();
  // I18N-2B: menu chrome + registry labels resolve via 'common'.
  const { t } = useTranslation('common');
  const { entitlement, entitlementReady } = useEntitlements();
  // Resolve whether the ACTIVE context is a personal home ("Hogar") or a condo.
  const activeContext = (contexts || []).find((c) => c.id === activeContextId);
  const activeUnit = ownedUnits.find((u) => u.id === activeOwnedUnitId);
  // FIX 2026-08-31 (Emily / Compras missing on mobile): the desktop home lens is
  // URL-driven (LensDashboard: location.pathname.startsWith('/home')) and does
  // NOT require an active owned unit. AppShell's "Hogar" tab
  // (handleHomeLensChange) clears activeOwnedUnitId for condo-linked units
  // (standalone units stay selected since the 2026-09-03 fix), so a
  // state-only isHomeContext could stay false on mobile and homeOnly items
  // (Compras, Tareas, Presupuesto, Vehículos, Mascotas) was filtered out of the
  // hamburger even while the user was ON /home/*. Mirror the desktop lens rule
  // here so both registries gate identically.
  const location = useLocation();
  // FIX 2026-09-03 (Jaime-approved, context mismatch): the mobile lens must derive
  // from the SAME URL rule as the desktop LensDashboard, with the URL taking
  // PRECEDENCE. A condo-linked unit sets activeCondoId, so the auth-state OR-terms
  // below previously rendered the condo menu on /home/* from the hamburger while
  // desktop correctly showed the home menu. Auth-state signals are now only a
  // fallback for non-/home routes.
  const isHomeContext = location.pathname.startsWith('/home')
    || (!location.pathname.startsWith('/building') && (
      activeContext?.kind === 'personal'
      || (!!activeUnit && !activeUnit.condo_id)
      || (!activeCondoId && !!activeOwnedUnitId)
    ));
  const navigationContext = isHomeContext ? 'home' : 'condo';
  // (Jaime 2026-08-31, TH1) Employee context is derived by the shared
  // isEmployeeContext helper (staff role OR any assigned unit), identical to the
  // desktop lens — the two menus can no longer drift.
  // FIX (Jaime bug 2026-09-09 PM, Francisco Caripa): lens-scoped gate — the
  // Hogar hamburger keeps Tareas/Compras/etc. unless the ACTIVE unit is
  // employee housing (assigned); a global 'empleado' role or a stale condo
  // work context (security+subType 'empleado') must not strip the own-home
  // menu. Condo lens keeps the full isEmployeeContext checks. Mirrors
  // LensDashboard exactly.
  const employeeContext = isEmployeeContextForLens(navigationContext, user, ownedUnits, activeUnit, activeContext);
  // FIX 2026-09-03 (Jaime, HARD RULE: desktop is the reference; mobile mirrors it
  // exactly per role/context). ROOT CAUSE of the condo-leak: the hamburger
  // filtered the flat HAMBURGER_NAVIGATION list, which contains condo modules
  // (Muro, Votaciones, Finanzas, ...) whose routes.home exist — so any user whose
  // entitlement covered them saw condo modules inside the Hogar (home) menu.
  // Mobile now derives from the SAME source as desktop: DESKTOP_NAVIGATION[context]
  // run through the SHARED filterNavigation chain (identical filtering), plus the
  // registry-driven fixed pinned entries (Mensajes + Quejas y Sugerencias,
  // alwaysVisible/ungated) and the intentionally mobile-only 'Mi perfil' item.
  // (2026-09-05 legacy strip) The hard-coded employee lens branch is GONE —
  // one rule set: the registry chain applies the same entitlement/permission/
  // role gates to everyone, mirroring the desktop gate exactly. employeeContext
  // still drives hiddenInEmployeeContext items (Rentas/Rotación, Jaime
  // 2026-09-07); 'Mis Pagos' was removed from all menus 2026-09-07.
  const isSecurityUser = user?.role === 'security';
  const registryByKeys = (keys: string[]): NavigationItem[] =>
    HAMBURGER_NAVIGATION.filter((item) => keys.includes(item.key));
  let contextItems: NavigationItem[];
  // (2026-09-09, F136) A condo 'security' membership with sub_type 'empleado'
  // is an employee work context: it gets the F136 employee keep-list via the
  // shared filterNavigation chain, NOT the plain security-lens trio (which
  // lacks Mi acceso/Reglamentos/Pánico/Emergencias and shows Llave Digital).
  if (isSecurityUser && navigationContext === 'condo' && !employeeContext) {
    // Mirror LensDashboard's security lens ('Seguridad': Escáner QR, Registro
    // Visitas, Llave Digital — all registry securityOnly items now).
    contextItems = registryByKeys(['verify', 'visitors', 'smart-access']);
  } else {
    contextItems = DESKTOP_NAVIGATION[navigationContext].flatMap((group) => group.items);
  }
  // (2026-09-23, QA bugs #89/#91) Standalone-home lens mirrors the desktop
  // LensDashboard gate exactly: no usuariosOnly strip and plan-baseline home
  // ids for navigation when the active unit has no condo linkage.
  const isStandaloneHomeLens = navigationContext === 'home' && !(user?.condoId)
    && isStandaloneHomeUnit(activeUnit, ownedUnits);
  const menuItems = filterNavigation(contextItems, {
    entitlement: isStandaloneHomeLens ? entitlementForStandaloneHome(entitlement) : entitlement,
    employeeContext, entitlementReady, user, roles, context: navigationContext,
    // (2026-09-03) usuariosOnly applies ONLY to the Hogar context (non-residente
    // Propietario); the Condominio hamburger is untouched. (2026-09-23, bug #89/#91)
    // Standalone homes are exempt — they keep their unit functions.
    usuariosOnly: navigationContext === 'home' && entitlement?.usuariosOnly === true && !isStandaloneHomeLens,
    isPermitted: (item) => !item.moduleId || item.moduleId === 'dashboard' || can(domainForModule(item.moduleId)),
  })
    .filter((item) => !item.alwaysVisible) // fixed pinned entries render in the footer below
    .concat(HAMBURGER_NAVIGATION.filter((item) => item.key === 'profile')); // Mi perfil: intentionally mobile-only (see registry)
  const fixedItems = FIXED_SIDEBAR_ITEM_KEYS
    .map((key) => HAMBURGER_NAVIGATION.find((item) => item.key === key))
    .filter((item): item is NavigationItem => !!item);
  // FIX 2026-09-03 (Jaime): de-dupe membership condo options against owned units on
  // normalized unit identity (see AppShell) so a unit-linked resident cannot pick a
  // near-identical "condo:" row that bounces them to /building/dashboard.
  const normUnit = (v?: string | null) => (v || '').toString().trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  // FIX 2026-09-07 (extension): /auth/me now surfaces standalone (condo-less)
  // memberships with condoId=null/condoName=null; those rendered as "- Casa Tajalí"
  // style rows under "Mis condominios" and a condo:null pick fell through to the
  // persisted active condo. Condo-less homes are already covered by the unit:
  // entries under "Mis hogares" (unlinkedOwnedUnits), so require m.condoId here.
  // FIX 2026-09-16 (live-QA, items 4/5): users with NO memberships and NO owned
  // units (claimed visitors / no-unit signups) get a minimal hamburger: only
  // "Mi acceso" (+ fixed pinned/footer entries), never the condo/home module
  // list whose pages 400 without a membership or unit.
  const minimalMode = (memberships || []).length === 0 && (ownedUnits || []).length === 0
    && !['super_admin', 'security', 'empleado'].includes(String(user?.role || ''));
  const visibleMemberships = (memberships || []).filter((m) => m.condoId && !ownedUnits.some((u) => {
    if (m.unitId && u.id === m.unitId) return true;
    if (!u.condo_id || m.condoId !== u.condo_id) return false;
    // Unit-less membership row for a condo that already has a linked owned unit is a redundant duplicate.
    if (!m.unitId && !m.unitNumber && !m.unitLabel) return true;
    return [m.unitNumber, m.unitLabel].some((label) => !!label && [u.unit_number, u.household_name].some((c) => normUnit(label) === normUnit(c)));
  }));
  const linkedOwnedUnits = ownedUnits.filter((u) => u.condo_id);
  const unlinkedOwnedUnits = ownedUnits.filter((u) => !u.condo_id);
  // FIX 2026-09-22 (Jaime bug 19:21/19:41, standalone-home resident): mirror
  // AppShell — standalone-home memberships (condo_id NULL + unit_id set, e.g.
  // unit 46B for Isa) ARE memberships: they render under "Mis hogares", select
  // the unit-scoped HOME lens via the existing X-Active-Unit-Id flow, and keep
  // minimalMode off (raw memberships length already covers that here, but the
  // switcher must also list them). Defensive shape handling: unitId/unit_id,
  // household_name/householdName.
  const standaloneHomeMemberships = (memberships || []).filter((m: any) => {
    const unitId = m.unitId || m.unit_id;
    if (!unitId) return false;
    if (m.condoId || m.condo_id) return false;
    if (ownedUnits.some((u) => u.id === unitId)) return false;
    return true;
  });
  const standaloneHomeLabel = (m: any) => {
    const unitId = m.unitId || m.unit_id;
    const owned = ownedUnits.find((u) => u.id === unitId);
    if (owned) return owned.household_name || owned.unit_number || t('shell.contexts.myHome');
    return m.householdName || m.household_name || m.unitLabel || m.unitNumber || m.unit_number || t('shell.contexts.myHome');
  };
  const navigate = useNavigate();
  // FIX #92 (2026-09-23): personal (Hogar) context support in the mobile drawer.
  const { activatePersonalContext } = useAuth();
  const personalContext = contexts.find((c) => c.kind === 'personal');
  // 2026-09-24 (Jaime 12:05 refinement, RESIDENCY gating, desktop parity):
  // hide 'Personal' only for resident/familiar unit-bound users.
  // Jaime directive (2026-10-01, TH1 personal-lens lane): a user with a
  // condo-linked owned unit must NEVER be offered the pure 'Mi hogar
  // (Personal)' context in the picker — it leaks condo state and caused the
  // TH1 wipe. The personal context row stays available internally for
  // standalone-only users; unit-linked users never see or auto-activate it.
  const showPersonalContext = Boolean(personalContext) && showPersonalFlag && !ownedUnits.some((u) => !!u.condo_id);
  const switcherValue = activeOwnedUnitId ? `unit:${activeOwnedUnitId}` : activeCondoId ? `condo:${activeCondoId}` : (personalContext && activeContextId === personalContext.id) ? 'personal' : '';

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKeyDown); document.body.style.overflow = ''; };
  }, [isOpen]);

  const changeContext = async (value: string) => {
    // FIX 2026-09-03 (escalation): residents with no owned units see ONLY condo:
    // membership rows in this drawer — picking one is a home-unit switch, not a
    // condo-management selection. Mirror desktop: residents land on
    // /home/dashboard; every other role keeps the explicit condo selection
    // behavior (/building/dashboard).
    if (value === 'personal') {
      // FIX #92 (2026-09-23): pure personal (Hogar) context — bind the session
      // JWT to the personal account context and land on the home lens.
      setActiveOwnedUnit(undefined);
      await setActiveCondo(null);
      navigate('/home/dashboard');
      setIsOpen(false);
      return;
    }
    if (value.startsWith('condo:')) {
      const condoId = value.slice(6);
      const membership = (memberships || []).find((entry) => entry.condoId === condoId);
      const residentPick = user?.role === 'resident' || membership?.role === 'resident';
      // Jaime 2026-09-30: setActiveCondo now restores the LAST-SELECTED unit
      // of the target condo (per-condo memory); do not wipe it here.
      await setActiveCondo(condoId);
      if (residentPick) {
        localStorage.setItem('vecindus_lens', 'home');
        navigate('/home/dashboard');
      } else {
        navigate('/building/dashboard');
      }
    }
    if (value.startsWith('unit:')) {
      // FIX 2026-09-22: the pick may be a standalone-home MEMBERSHIP (no owned
      // unit) — same unit-scoped home flow via X-Active-Unit-Id.
      const unitId = value.slice(5);
      // #178 (2026-09-25): the pick may also be a CONDO-LINKED membership unit
      // (resident with 2+ units in one condo). Resolve the condo from that row,
      // and persist the unit-level account context id (vecindus_active_context)
      // so the selection survives reloads.
      const unit = ownedUnits.find((entry) => entry.id === unitId);
      const standaloneMember = !unit ? standaloneHomeMemberships.find((m: any) => (m.unitId || m.unit_id) === unitId) : undefined;
      const linkedMember = !unit && !standaloneMember ? (memberships || []).find((m) => (m.unitId || (m as unknown as Record<string, unknown>).unit_id) === unitId) : undefined;
      if (!unit && !standaloneMember && !linkedMember) return;
      const memberCondoId = (linkedMember?.condoId || ((linkedMember ? (linkedMember as unknown as Record<string, unknown>).condo_id : null) as string | null)) || null;
      setActiveOwnedUnit(unitId);
      await setActiveCondo(unit?.condo_id || memberCondoId || null, true);
      const unitContext = (contexts || []).find((c) => c.kind !== 'personal' && (c.unitId === unitId || (c as unknown as Record<string, unknown>).unit_id === unitId));
      if (unitContext?.id) localStorage.setItem('vecindus_active_context', unitContext.id);
      // (Jaime 2026-08-31) Unit switch always lands on /home/dashboard,
      // except security/employee staff who keep the condo view.
      // FIX 2026-09-03 (Jaime): mirror the desktop handleContextChange exactly —
      // security/employee with a condo-linked unit keep the condo view; everyone
      // else lands on /home/dashboard and stays there (home lens now preserved by
      // AuthContext.setActiveCondo when preserveOwnedUnit=true).
      // FIX 2026-09-07: backend role is 'empleado'; the old 'employee'
      // comparison was dead and never matched.
      if (unit?.condo_id && (user?.role === 'security' || user?.role === 'empleado')) {
        navigate('/building/dashboard');
      } else {
        navigate('/home/dashboard');
      }
    }
    setIsOpen(false);
  };

  return (
    <>
      <button type="button" onClick={() => setIsOpen(true)} aria-label={t('shell.menu.open')} aria-expanded={isOpen} aria-controls="mobile-navigation"
        className="md:hidden inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-500)]">
        <Menu className="h-6 w-6" />
      </button>
      {isOpen && <button type="button" aria-label={t('shell.menu.close')} onClick={() => setIsOpen(false)} className="mobile-menu-backdrop md:hidden fixed inset-0 z-[60]" />}
      <aside id="mobile-navigation" role="dialog" aria-label={t('shell.menu.navAria')} aria-modal="true"
        className={`md:hidden fixed inset-y-0 left-0 z-[70] flex w-[min(88vw,22rem)] flex-col bg-[var(--v-surface-1)] text-[var(--v-text-primary)] shadow-2xl transition-transform duration-200 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center justify-between border-b border-[var(--v-border-light)] px-5 py-4">
          <div><p className="font-bold">Vecindus <AppVersionBadge /></p><p className="truncate text-xs text-[var(--v-text-secondary)]">{user?.name || t('shell.sidebar.account')}</p></div>
          <button type="button" onClick={() => setIsOpen(false)} aria-label={t('shell.menu.close')} className="rounded-lg p-2 hover:bg-[var(--v-surface-2)]"><X className="h-5 w-5" /></button>
        </div>
        {(visibleMemberships.length > 0 || ownedUnits.length > 0 || standaloneHomeMemberships.length > 0 || personalContext) && <div className="border-b border-[var(--v-border-light)] p-4">
          <label htmlFor="mobile-context" className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[var(--v-text-secondary)]">{t('shell.contexts.change')}</label>
          <select id="mobile-context" aria-label={t('shell.contexts.changeAria')} value={switcherValue} onChange={(e) => changeContext(e.target.value).catch(() => undefined)} className="w-full min-w-0 rounded-xl border border-[var(--v-border-light)] bg-[var(--v-surface-2)] px-3 py-3 text-sm text-[var(--v-text-primary)]">
            <option value="" disabled>{t('shell.contexts.select')}</option>
            {(visibleMemberships.length > 0 || linkedOwnedUnits.length > 0) && <optgroup label={t('shell.contexts.myCondos')}>{visibleMemberships.map((m) => { const mu = m.unitId || ((m as unknown as Record<string, unknown>).unit_id as string | undefined); const val = mu ? `unit:${mu}` : `condo:${m.condoId}`; return <option key={val} value={val}>{m.condoName} · {m.unitLabel || m.unitNumber || t('shell.contexts.noUnit')}</option>; })}{linkedOwnedUnits.map((u) => <option key={`unit:${u.id}`} value={`unit:${u.id}`}>{u.condo_name} · {u.household_name || u.unit_number}</option>)}</optgroup>}
            {(unlinkedOwnedUnits.length > 0 || standaloneHomeMemberships.length > 0 || showPersonalContext) && <optgroup label={t('shell.contexts.myHomes')}>{showPersonalContext && <option key="personal" value="personal">{t('shell.contexts.myHome')} (Personal)</option>}{unlinkedOwnedUnits.map((u) => <option key={`unit:${u.id}`} value={`unit:${u.id}`}>{u.household_name || u.unit_number} · {t('shell.contexts.unlinked')}</option>)}{standaloneHomeMemberships.map((m: any) => { const unitId = m.unitId || m.unit_id; return <option key={`smember:${unitId}`} value={`unit:${unitId}`}>{standaloneHomeLabel(m)} · {t('shell.contexts.myHome')}</option>; })}</optgroup>}
          </select>
        </div>}
        <nav className="flex-1 overflow-y-auto p-4" aria-label={t('shell.menu.sectionsAria')}>
          {minimalMode && (
            <button type="button" onClick={() => { navigate('/home/my-qr'); setIsOpen(false); }} className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm hover:bg-[var(--v-surface-2)]"><Home className="h-5 w-5" aria-hidden="true" /><span className="flex-1">{t('shell.lens.myAccess')}</span><ChevronRight className="h-4 w-4 text-[var(--v-text-secondary)]" /></button>
          )}
          {!minimalMode && permissionsLoading
            ? [0, 1, 2, 3].map((i) => <div key={`skeleton-${i}`} aria-hidden="true" className="mb-1 min-h-11 w-full animate-pulse rounded-xl bg-[var(--v-surface-2)]" />)
            : menuItems.map((item) => <button key={item.key} type="button" onClick={() => { navigate(item.routes[navigationContext]); setIsOpen(false); }} className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm hover:bg-[var(--v-surface-2)]"><item.icon className="h-5 w-5" aria-hidden="true" /><span className="flex-1">{t(navLabelKey(item.key), { defaultValue: item.label })}</span><ChevronRight className="h-4 w-4 text-[var(--v-text-secondary)]" /></button>)}
        </nav>
        {/* Fixed pinned entries (2026-09-03): Mensajes + Quejas y Sugerencias —
            registry-driven, alwaysVisible/ungated for ALL users, mirroring the
            desktop IconGridSidebar footer placement. */}
        {fixedItems.length > 0 && <div className="flex-shrink-0 border-t border-[var(--v-border-light)] px-4 pt-3 pb-1">
          {/* Presentation matches desktop's fixed plain buttons (Jaime 00:57): icon + label, no submenu chevron. */}
          {fixedItems.map((item) => <button key={item.key} type="button" onClick={() => { navigate(item.routes[navigationContext]); setIsOpen(false); }} className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm hover:bg-[var(--v-surface-2)]"><item.icon className="h-5 w-5" aria-hidden="true" /><span className="flex-1">{t(navLabelKey(item.key), { defaultValue: item.label })}</span>{item.key === 'mensajes' && messagesUnread > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="mensajes-unread-badge">{messagesUnread > 99 ? '99+' : messagesUnread}</span>}</button>)}
        </div>}
        <div className="border-t border-[var(--v-border-light)] p-4"><button type="button" onClick={() => { logout(); setIsOpen(false); }} className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm text-[var(--v-danger)] hover:bg-[var(--v-danger-bg)]"><LogOut className="h-5 w-5" />{t('shell.header.logout')}</button></div>
      </aside>
    </>
  );
}
